import { useCallback } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  tcSelectedDevAreaAtom,
  unitOverridesAtom,
  tcUnitRotationPendingAtom,
  tcUnitResolutionRefreshAtom,
  unitAzimuthOverridesAtom,
} from 'pages/type-curves/type-curve-filters/atoms';
import { azimuthOverrideKey } from 'pages/type-curves/azimuth-override';
import {
  useCreateUnitOverrideMutation,
  useUndoUnitOverrideMutation,
} from 'pages/type-curves/use-unit-overrides';
import { activeTcWorkspaceAtom } from '../atoms';
import { toastAtom } from 'pages/type-curves/atoms';

export interface UnitRotation {
  /** Whether the selected unit currently carries a rotate override. */
  isRotated: boolean;
  /** A rotate or un-rotate is in flight. */
  pending: boolean;
  /** No unit selected, so there is nothing to rotate. */
  disabled: boolean;
  /** Toggle the selected unit's LTD direction by 90°. */
  toggle: () => Promise<void>;
}

/**
 * Rotate / un-rotate the selected unit's LTD direction on a Type Curve workspace.
 *
 * The backend stores the resulting absolute bearing, not a delta, and allows one
 * rotate per unit until it is undone.
 *
 * The map layer draws whatever `unitAzimuthOverridesAtom` states for the unit,
 * turning its own tile-built frame onto it. A rotate states the bearing it
 * requested. This route's undo answers 204 with no bearing, so an un-rotate
 * clears the statement and re-requests the tiles, which then bake the restored
 * bearing; the line settles once they arrive. (The Inventory Workspace route
 * states the bearing on undo too, so its builder never waits on tiles.)
 *
 * @return {UnitRotation} Current rotation state and the toggle.
 */
export function useUnitRotation(): UnitRotation {
  const selected = useAtomValue(tcSelectedDevAreaAtom);
  const overrides = useAtomValue(unitOverridesAtom);
  const pending = useAtomValue(tcUnitRotationPendingAtom);
  const workspace = useAtomValue(activeTcWorkspaceAtom);
  const setToast = useSetAtom(toastAtom);
  const azimuthOverrides = useAtomValue(unitAzimuthOverridesAtom);
  const setAzimuthOverrides = useSetAtom(unitAzimuthOverridesAtom);
  const bumpRefresh = useSetAtom(tcUnitResolutionRefreshAtom);

  const createOverride = useCreateUnitOverrideMutation(workspace);
  const undoOverride = useUndoUnitOverrideMutation(workspace);

  const selectedId = selected?.developmentAreaId ?? null;
  const rotateOverride = overrides.find(
    (o) => o.action === 'rotate' && o.unitId === selectedId
  );

  const toggle = useCallback(async () => {
    if (selectedId === null) return;
    const key = azimuthOverrideKey(workspace?.id, selectedId);

    if (rotateOverride) {
      // Un-rotate: LTD direction only — skip the dev-areas re-fetch + loader.
      const result = await undoOverride(rotateOverride.id, {
        refreshOverlaps: false,
      });
      if (!result.ok) {
        setToast({
          kind: 'error',
          title: 'Failed to rotate unit',
          subtitle: result.message,
        });
        return;
      }
      setAzimuthOverrides((prev) => {
        const next = new Map(prev);
        next.delete(key);
        return next;
      });
      // Tiles re-request and bake the restored bearing; the layer refreshes its
      // cached frame from them.
      bumpRefresh((n) => n + 1);
      return;
    }

    // Measured from where the LTDs are now: the last stated bearing, else the tile's.
    const fromDeg = azimuthOverrides.get(key) ?? selected?.azimuthDeg ?? 0;
    const targetAzimuth = (fromDeg + 90) % 360;
    const result = await createOverride({
      unit_id: selectedId,
      action: 'rotate',
      azimuth_rotation_angle_deg: targetAzimuth,
    });
    if (!result.ok) {
      setToast({
        kind: 'error',
        title: 'Failed to rotate unit',
        subtitle: result.message,
      });
      return;
    }
    setAzimuthOverrides((prev) => new Map(prev).set(key, targetAzimuth));
  }, [
    selectedId,
    selected?.azimuthDeg,
    workspace?.id,
    rotateOverride,
    undoOverride,
    createOverride,
    setToast,
    azimuthOverrides,
    setAzimuthOverrides,
    bumpRefresh,
  ]);

  return {
    isRotated: Boolean(rotateOverride),
    pending,
    disabled: selectedId === null,
    toggle,
  };
}
